<template>
  <v-container fluid class="pa-0 pa-md-4 py-12 py-md-4">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-btn icon="mdi-arrow-left" variant="text" @click="goBack" class="mr-2" />
        <div class="d-flex justify-space-between align-center text-h5 font-weight-bold">
          Add New Vehicle
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Step 1: Entry Mode -->
        <div v-if="step === 'vin-input'" class="pa-8">
          <div class="text-h5 mb-2 text-center">Add Vehicle</div>
          <div class="text-caption text-grey mb-6 text-center">
            Start by entering a VIN, or search by Year/Make/Model to pick a trim.
          </div>
          <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
              <v-btn-toggle
                v-model="entryMode"
                mandatory
                density="comfortable"
                class="mb-4 d-flex justify-center"
              >
                <v-btn value="vin" variant="outlined">VIN</v-btn>
                <v-btn value="ymm" variant="outlined">Year / Make / Model</v-btn>
              </v-btn-toggle>

              <div v-if="entryMode === 'vin'">
              <v-text-field
                v-model="vinInput"
                label="VIN"
                placeholder="Enter 17-character VIN"
                variant="outlined"
                :loading="isLoadingPrefill"
                :error-messages="vinError"
                :maxlength="17"
                class="mb-4"
                @input="vinError = ''"
              />
              </div>

              <div v-else>
                <v-text-field
                  v-model="ymmYear"
                  label="Year"
                  placeholder="e.g., 2024"
                  variant="outlined"
                  type="number"
                  class="mb-3"
                />
                <v-text-field
                  v-model="ymmMake"
                  label="Make"
                  placeholder="e.g., Ford"
                  variant="outlined"
                  class="mb-3"
                />
                <v-text-field
                  v-model="ymmModel"
                  label="Model"
                  placeholder="e.g., Escape"
                  variant="outlined"
                  class="mb-4"
                />
              </div>

              <div class="d-flex justify-center" style="gap: 6px">
                <v-btn variant="outlined" @click="goBack">Cancel</v-btn>
                <v-btn
                  color="primary"
                  :loading="isLoadingPrefill"
                  :disabled="entryMode === 'vin' ? (!vinInput || vinInput.length !== 17) : (!ymmYear || !ymmMake || !ymmModel)"
                  @click="continueFromEntry"
                >
                  Continue
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Step 2: Enhancement Select -->
        <div v-if="step === 'enhancement-select'" class="pa-8">
          <div class="text-h5 mb-2 text-center">Select Enhancement</div>
          <div class="text-caption text-grey mb-6 text-center">
            Choose an enhancement source to prefill empty fields.
          </div>

          <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
              <v-alert v-if="enhancementsError" type="warning" class="mb-4" density="comfortable">
                {{ enhancementsError }}
              </v-alert>

              <v-alert v-if="enhancementOptions.length === 0" type="info" class="mb-4" density="comfortable">
                No enhancements are configured for this dealer. You can continue and add the vehicle manually.
              </v-alert>

              <v-select
                v-if="enhancementOptions.length > 0"
                v-model="selectedEnhancementId"
                label="Enhancement"
                variant="outlined"
                :items="enhancementOptions"
                item-title="title"
                item-value="value"
                :loading="isLoadingPrefill"
                class="mb-4"
              />

              <div class="d-flex justify-center flex-wrap" style="gap: 6px">
                <v-btn variant="outlined" @click="resetToEntry">Back</v-btn>
                <v-btn
                  variant="outlined"
                  color="grey"
                  @click="continueManualFromEnhancement"
                >
                  Continue Manually
                </v-btn>
                <v-btn
                  color="primary"
                  :loading="isLoadingPrefill"
                  :disabled="enhancementOptions.length > 0 && !selectedEnhancementId"
                  @click="continueFromEnhancement"
                >
                  Continue
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Step 3: Trim Selection (Chrome + Y/M/M) -->
        <div v-if="step === 'trim-select'" class="pa-8">
          <div class="text-h5 mb-2 text-center">Select Trim</div>
          <div class="text-caption text-grey mb-6 text-center">
            {{ ymmYear }} {{ ymmMake }} {{ ymmModel }}
          </div>

          <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
              <v-alert v-if="ymmError" type="warning" class="mb-4" density="comfortable">
                {{ ymmError }}
              </v-alert>

              <v-select
                v-model="selectedTrimId"
                label="Trim"
                variant="outlined"
                :items="trimOptions"
                item-title="title"
                item-value="value"
                :loading="isLoadingPrefill"
                class="mb-4"
              />

              <div class="d-flex justify-center flex-wrap" style="gap: 6px">
                <v-btn variant="outlined" @click="backToEnhancement">Back</v-btn>
                <v-btn
                  color="primary"
                  :loading="isLoadingPrefill"
                  :disabled="!selectedTrimId"
                  @click="continueFromTrim"
                >
                  Continue
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Step 4: Vehicle Form -->
        <div v-if="step === 'form'" class="pa-4">
          <v-alert v-if="prefillStatus === 'success'" type="success" class="mb-4" dismissible>
            {{ prefillMessage || 'Vehicle data successfully prefilled from VIN!' }}
          </v-alert>
          <v-alert v-else-if="prefillStatus === 'error'" type="warning" class="mb-4" dismissible>
            {{
              prefillMessage ||
              'We could not prefill data. Please review and complete the form manually.'
            }}
          </v-alert>

          <v-form ref="formRef" @submit.prevent="saveVehicle">
            <v-row>
              <!-- Dynamic Fields -->
              <v-col v-if="isLoadingFields" cols="12" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" />
                <div class="mt-4">Loading vehicle fields...</div>
              </v-col>

              <template v-else>
                <!-- A6 operational inventory attributes (manual vehicles) -->
                <v-col cols="12">
                  <v-card variant="outlined" class="pa-4">
                    <div class="text-subtitle-1 font-weight-bold mb-2">Inventory lifecycle</div>
                    <div class="text-body-2 text-grey mb-3">
                      Choose the canonical inventory attributes for this vehicle. Inventory State is
                      projected to the website State field after publish.
                    </div>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="vehicleData.inventoryState"
                          :items="inventoryStateOptions"
                          item-title="title"
                          item-value="value"
                          label="Inventory State"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                        />
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-select
                          v-model="vehicleData.inventoryUsageType"
                          :items="inventoryUsageTypeOptions"
                          item-title="title"
                          item-value="value"
                          label="Usage Type"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                        />
                      </v-col>

                      <v-col cols="12" md="4">
                        <div class="text-body-2 font-weight-medium mb-1">
                          Website Visibility after publish
                        </div>
                        <v-radio-group v-model="initialWebsiteVisibility" inline hide-details>
                          <v-radio label="Visible" :value="WEBSITE_VISIBILITY.VISIBLE" />
                          <v-radio label="Hidden" :value="WEBSITE_VISIBILITY.HIDDEN" />
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <v-row v-if="initialWebsiteVisibility === WEBSITE_VISIBILITY.HIDDEN" class="mt-1">
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="selectedWebsiteVisibilityReason"
                          :items="websiteVisibilityReasonOptions"
                          item-title="label"
                          item-value="value"
                          label="Initial hidden reason"
                          variant="outlined"
                          density="comfortable"
                          :error="Boolean(uiFieldErrors['selectedWebsiteVisibilityReason'])"
                          :error-messages="uiFieldErrors['selectedWebsiteVisibilityReason'] ? [uiFieldErrors['selectedWebsiteVisibilityReason']] : []"
                          @update:model-value="clearFieldError('selectedWebsiteVisibilityReason')"
                        />
                      </v-col>

                      <v-col v-if="selectedWebsiteVisibilityReason === 'OTHER'" cols="12" md="6">
                        <v-text-field
                          v-model="websiteVisibilityReasonNotes"
                          label="Please specify"
                          variant="outlined"
                          density="comfortable"
                          :error="Boolean(uiFieldErrors['websiteVisibilityReasonNotes'])"
                          :error-messages="uiFieldErrors['websiteVisibilityReasonNotes'] ? [uiFieldErrors['websiteVisibilityReasonNotes']] : []"
                          @input="clearFieldError('websiteVisibilityReasonNotes')"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>

                <!-- Dynamically grouped fields by section -->
                <template v-for="(section, sectionIndex) in fieldSections" :key="sectionIndex">
                  <v-col v-if="section.fields.length > 0" cols="12">
                    <v-divider class="my-2" />
                    <div class="text-subtitle-1 font-weight-bold text-primary mt-4 mb-2">
                      {{ section.label }}
                    </div>
                  </v-col>

                  <!-- Put VIN + Condition into the same Essential Information section -->
                  <v-col v-if="section.label === 'Essential Information'" cols="12">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="vehicleData.vin"
                          label="VIN *"
                          variant="outlined"
                          density="comfortable"
                          :error="Boolean(uiFieldErrors['vin'])"
                          :error-messages="uiFieldErrors['vin'] ? [uiFieldErrors['vin']] : []"
                          @input="clearFieldError('vin')"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="vehicleData.condition"
                          label="Condition *"
                          variant="outlined"
                          density="comfortable"
                          :items="conditionOptions"
                          :error="Boolean(uiFieldErrors['condition'])"
                          :error-messages="uiFieldErrors['condition'] ? [uiFieldErrors['condition']] : []"
                          @update:model-value="clearFieldError('condition')"
                        />
                      </v-col>
                      <v-col cols="12" md="2" class="d-flex align-center">
                        <v-checkbox
                          v-if="showIsCertified"
                          v-model="isCertifiedUi"
                          label="Is Certified?"
                          density="comfortable"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-combobox
                          v-model="additionalConditions"
                          label="Additional conditions (optional)"
                          variant="outlined"
                          density="comfortable"
                          multiple
                          chips
                          closable-chips
                          clearable
                          hint='Add tags like "Nissan Certified", "One Owner", etc.'
                          persistent-hint
                          @update:model-value="additionalConditionsTouched = true"
                        />
                      </v-col>
                    </v-row>
                  </v-col>

                  <template v-for="field in section.fields" :key="field">
                    <!-- Skip conditional fields that don't match -->
                    <v-col
                      v-if="shouldShowField(field)"
                      cols="12"
                      :md="getFieldInputType(field) === 'textarea' ? 12 : 6"
                    >
                      <!-- Boolean/Checkbox fields -->
                      <v-checkbox
                        v-if="getFieldInputType(field) === 'boolean'"
                        v-model="vehicleData[field]"
                        :label="formatFieldLabel(field)"
                        density="comfortable"
                        hide-details
                      />

                      <!-- Number fields -->
                      <v-text-field
                        v-else-if="getFieldInputType(field) === 'number'"
                        v-model.number="vehicleData[field]"
                        :label="formatFieldLabel(field)"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        :min="getNumberConstraints(field)?.min"
                        :max="getNumberConstraints(field)?.max"
                        :step="getNumberConstraints(field)?.step"
                        :error="Boolean(uiFieldErrors[field])"
                        :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                        @input="clearFieldError(field)"
                      />

                      <!-- Textarea fields -->
                      <v-textarea
                        v-else-if="getFieldInputType(field) === 'textarea'"
                        v-model="vehicleData[field]"
                        :label="formatFieldLabel(field)"
                        variant="outlined"
                        density="comfortable"
                        rows="3"
                        auto-grow
                        :error="Boolean(uiFieldErrors[field])"
                        :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                        @input="clearFieldError(field)"
                      />

                      <!-- Select/Dropdown fields -->
                      <v-select
                        v-else-if="getFieldInputType(field) === 'select'"
                        v-model="vehicleData[field]"
                        :label="formatFieldLabel(field)"
                        variant="outlined"
                        density="comfortable"
                        :items="getDropdownOptions(field) || []"
                        clearable
                        :error="Boolean(uiFieldErrors[field])"
                        :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                        @update:model-value="clearFieldError(field)"
                      />

                      <!-- Default text fields -->
                      <v-text-field
                        v-else
                        v-model="vehicleData[field]"
                        :label="formatFieldLabel(field)"
                        variant="outlined"
                        density="comfortable"
                        :error="Boolean(uiFieldErrors[field])"
                        :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                        @input="clearFieldError(field)"
                      />
                    </v-col>
                  </template>

                  <!-- Photos (show right after Essential Information) -->
                  <v-col v-if="section.label === 'Essential Information'" cols="12">
                    <v-card variant="outlined" class="pa-4">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div class="text-subtitle-1 font-weight-bold">Photos</div>
                        <v-menu>
                          <template #activator="{ props: menuProps }">
                            <v-btn
                              variant="outlined"
                              color="primary"
                              type="button"
                              v-bind="menuProps"
                            >
                              <v-icon start icon="mdi-plus" />
                              Add Photos
                            </v-btn>
                          </template>
                          <v-list density="compact">
                            <v-list-item
                              prepend-icon="mdi-folder"
                              title="Choose from Files"
                              @click="triggerPhotoSelection"
                            />
                            <v-list-item
                              prepend-icon="mdi-camera"
                              title="Take Photo"
                              @click="openCameraDialog"
                            />
                          </v-list>
                        </v-menu>
                      </div>
                      <input
                        ref="photoInputRef"
                        type="file"
                        accept="image/*"
                        multiple
                        class="d-none"
                        @change="handlePhotoSelection"
                      />
                      <CameraCaptureDialog
                        v-model="cameraDialogOpen"
                        @capture="handleCameraCapture"
                      />
                      <div
                        v-if="photos.length === 0"
                        class="d-flex align-center justify-center bg-grey-lighten-4 pa-6 text-center rounded"
                      >
                        <div>
                          <v-icon icon="mdi-image-multiple" size="48" color="grey" class="mb-2" />
                          <div class="text-body-2 text-grey">
                            No images selected yet. You can upload multiple images. Name is optional
                            (base name only) — we'll keep the original extension and generate a name if
                            left blank. Drag and drop rows to reorder.
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <v-table density="comfortable" class="photo-table">
                          <thead>
                            <tr>
                              <th class="text-nowrap" style="width: 56px"></th>
                              <th>Image</th>
                              <th class="text-nowrap">Size</th>
                              <th class="text-end">Actions</th>
                            </tr>
                          </thead>
                          <Draggable
                            v-model="photos"
                            item-key="id"
                            handle=".drag-handle"
                            tag="tbody"
                            :animation="150"
                            ghost-class="drag-ghost"
                            chosen-class="drag-chosen"
                          >
                            <template #item="{ element: photo }">
                              <tr>
                                <td class="text-center">
                                  <v-icon
                                    icon="mdi-drag"
                                    color="grey"
                                    class="drag-handle"
                                    title="Drag to reorder"
                                  />
                                </td>
                                <td>
                                  <div class="d-flex align-center">
                                    <v-avatar size="48" class="mr-3" v-if="photo.previewUrl">
                                      <v-img :src="photo.previewUrl" cover />
                                    </v-avatar>
                                    <div class="flex-grow-1">
                                      <v-text-field
                                        v-model="photo.name"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        :placeholder="`Optional name (e.g. front)`"
                                        :suffix="photo.ext ? '.' + photo.ext : undefined"
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td class="text-nowrap">{{ formatFileSize(photo.file.size) }}</td>
                                <td class="text-end">
                                  <v-btn
                                    icon="mdi-close"
                                    variant="text"
                                    size="small"
                                    @click="removePhoto(photo.id)"
                                  />
                                </td>
                              </tr>
                            </template>
                          </Draggable>
                        </v-table>
                      </div>
                    </v-card>
                  </v-col>
                </template>
              </template>
            </v-row>

            <!-- Spacer so the last fields aren't hidden behind the fixed footer -->
            <div class="form-footer-spacer" />

            <!-- Sticky footer actions -->
            <div class="form-footer-sticky">
              <v-divider />
              <div class="form-footer-inner">
                <div class="d-flex justify-end ga-2">
                  <v-btn variant="outlined" @click="goBack">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    type="submit"
                    :loading="isSaving"
                    :disabled="isLoadingFields || !isFormValidForSave"
                  >
                    Save Vehicle
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </div>
      </v-card-text>
    </v-card>
  </v-container>

  <!-- Saving dialog -->
  <v-dialog v-model="savingDialog" persistent max-width="420">
    <v-card>
      <v-card-title class="text-h6">Saving Vehicle</v-card-title>
      <v-card-text>
        <div class="mb-4">{{ savingMessage }}</div>
        <v-progress-linear :model-value="savingProgress" height="8" color="primary" rounded />
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Enhancement failed dialog -->
  <v-dialog v-model="enhancementFailedDialog" max-width="520">
    <v-card>
      <v-card-title class="text-h6">Couldn’t load vehicle data</v-card-title>
      <v-card-text>
        <div class="mb-2">
          {{ userFriendlyEnhancementFailedMessage }}
        </div>
        <div class="text-body-2 text-grey">
          Would you like to continue and add the vehicle manually, or start over?
        </div>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" @click="startOverFromFailure">Start Over</v-btn>
        <v-btn color="primary" variant="flat" @click="continueManualFromFailure">
          Continue Manually
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { INVENTORY_STATE, INVENTORY_USAGE_TYPE, WEBSITE_VISIBILITY } from '@jazel-auto/ise-contracts/dist/inventory.js'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import { makeAlert } from '@/utils'
import { sanitizeAddVehicleEnhancementPrefill } from '@/utils/addVehicleEnhancementPrefill'
import { isExcludedFromSrpPatch } from '@/utils/a6LifecycleFields'
import { usePublishedReadyCourtesySetting } from '@/composables/usePublishedReadyCourtesySetting'
import { buildManualInventoryStateOptions } from '@/utils/manualInventoryStateOptions'
import Draggable from 'vuedraggable'
import CameraCaptureDialog from '@/components/CameraCaptureDialog.vue'
import { fetchIseMappingFields, extractIseMappingFieldKeys } from '@/services/fieldMappingService'
import {
  fetchVehicleByVin,
  type InventoryState,
  type InventoryUsageType,
  type WebsiteVisibility,
} from '@/services/inventory'
import {
  getManualAddEditDisplayFields,
  DISPLAY_FIELD_ORDER,
  EXCLUDED_FIELDS,
  PRIORITY_FIELDS,
  getFieldInputType,
} from '@/constants/vehicleFieldConfig'
import { getDropdownOptions, getNumberConstraints } from '@/constants/fieldOptions'

const router = useRouter()
const route = useRoute()
const accountSettings = useAccountSettingsStore()
const publishStore = usePublishStore()
const { separateReadyCourtesyVehicles } = usePublishedReadyCourtesySetting()

const step = ref<'vin-input' | 'enhancement-select' | 'trim-select' | 'form'>('vin-input')
const entryMode = ref<'vin' | 'ymm'>('vin')
const vinInput = ref('')
const vinError = ref('')
const isLoadingPrefill = ref(false)
const isLoadingFields = ref(false)
const isSaving = ref(false)
const prefillStatus = ref<'success' | 'error' | null>(null)
const prefillMessage = ref('')

type EnhancementOption = { value: string; title: string }
const enhancementOptions = ref<EnhancementOption[]>([])
const selectedEnhancementId = ref<string>('')
const enhancementsError = ref('')
const enhancementFailedDialog = ref(false)
const enhancementFailedMessage = ref('')

const userFriendlyEnhancementFailedMessage = computed(() => {
  const raw = String(enhancementFailedMessage.value || '').trim()
  if (!raw) {
    return "We couldn't load vehicle data from the selected source."
  }

  const lower = raw.toLowerCase()
  // Hide technical errors in the dialog body (HTTP codes, backend phrasing, etc.)
  if (
    lower.includes('datahub') ||
    lower.includes('status code') ||
    lower.includes('failed to fetch') ||
    lower.includes('http') ||
    lower.includes('request failed')
  ) {
    return "We couldn't load vehicle data right now. Please try again, continue manually, or start over."
  }

  // Keep intentional user-facing messages (e.g., "This enhancement requires a VIN...")
  return raw
})

const ymmYear = ref<string>('')
const ymmMake = ref<string>('')
const ymmModel = ref<string>('')
const ymmError = ref('')
type TrimOption = { value: string; title: string; trim: string; styleName: string; baseMsrp?: number | null; img?: string | null }
const trimOptions = ref<TrimOption[]>([])
const selectedTrimId = ref<string>('')

interface PhotoItem {
  id: string
  file: File
  // Base name only (without extension)
  name: string
  // File extension preserved from original selection (e.g., 'png', 'jpg')
  ext?: string
  previewUrl: string
}

const vehicleData = ref<Record<string, any>>({})
const availableFields = ref<string[]>([])
const displayFields = ref<string[]>([])
const fieldErrors = ref<Record<string, string>>({})

const endpoint = import.meta.env.VITE_ISE_BACKEND_URL
const jsonHeaders = () => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accountSettings.sessionToken) {
    headers.Authorization = `Bearer ${accountSettings.sessionToken}`
  }
  return headers
}

// Note: this ref is inside a v-for (section loop), so Vue will provide an array of elements.
const photoInputRef = ref<HTMLInputElement | HTMLInputElement[] | null>(null)
const photos = ref<PhotoItem[]>([])
const cameraDialogOpen = ref(false)

const savingDialog = ref(false)
const savingMessage = ref('Saving vehicle details...')
const savingProgress = ref(0)

const conditionOptions = [
  { title: 'New', value: 'new' },
  { title: 'Used', value: 'used' },
]

const inventoryStateOptions = computed(() =>
  buildManualInventoryStateOptions({
    includeReadyCourtesy: separateReadyCourtesyVehicles.value,
    includeArchived: true,
  }),
)

const inventoryUsageTypeOptions: Array<{ title: string; value: InventoryUsageType }> = [
  { title: 'Retail', value: INVENTORY_USAGE_TYPE.RETAIL },
  { title: 'Wholesale', value: INVENTORY_USAGE_TYPE.WHOLESALE },
  { title: 'Demo', value: INVENTORY_USAGE_TYPE.DEMO },
  { title: 'Loaner', value: INVENTORY_USAGE_TYPE.LOANER },
  { title: 'Company vehicle', value: INVENTORY_USAGE_TYPE.COMPANY_VEHICLE },
  { title: 'Service', value: INVENTORY_USAGE_TYPE.SERVICE },
]

const REQUIRED_FIELDS = ['vin', 'year', 'make', 'model', 'condition', 'price']

const additionalConditions = ref<string[]>([])
const additionalConditionsTouched = ref(false)
const hasCpoSignal = ref(false)
const hiddenCertificationFields = new Set(['certified_by_dealer', 'certified_by_manufacturer'])

const normalizeConditionValue = (c: unknown): 'new' | 'used' | '' => {
  const s = String(c ?? '').trim()
  if (!s) return ''
  const up = s.toUpperCase()
  if (up === 'N' || up === 'NEW') return 'new'
  if (up === 'U' || up === 'USED') return 'used'
  // Legacy/compat: treat CPO as used for manual vehicles.
  if (up === 'C' || up === 'CPO' || s.toLowerCase() === 'cpo') return 'used'
  // Already in our expected format
  if (s.toLowerCase() === 'new') return 'new'
  if (s.toLowerCase() === 'used') return 'used'
  return ''
}

const toConditionToken = (c: unknown): 'New' | 'Used' | '' => {
  const v = normalizeConditionValue(c)
  if (v === 'new') return 'New'
  if (v === 'used') return 'Used'
  return ''
}

const isTruthy = (v: unknown): boolean => {
  if (v === true) return true
  if (v === false || v == null) return false
  if (typeof v === 'number') return v !== 0
  const s = String(v).trim().toLowerCase()
  return s === '1' || s === 'true' || s === 'yes' || s === 'y'
}

const showIsCertified = computed(() => toConditionToken((vehicleData.value as any)?.condition) === 'Used')

const ensureDefaultOperationalInventoryFields = () => {
  const data = vehicleData.value as Record<string, unknown>
  if (data.inventoryState === undefined || data.inventoryState === null || String(data.inventoryState).trim() === '') {
    data.inventoryState = INVENTORY_STATE.READY_FOR_SALE
  }
  if (
    data.inventoryUsageType === undefined ||
    data.inventoryUsageType === null ||
    String(data.inventoryUsageType).trim() === ''
  ) {
    data.inventoryUsageType = INVENTORY_USAGE_TYPE.RETAIL
  }
}

// "Is Certified?" is a manual-entry UX toggle. It reads either cert flag (or legacy CPO)
// and writes dealer certification; unchecking clears both dealer+manufacturer to avoid hidden state.
const isCertifiedUi = computed<boolean>({
  get: () => {
    const dealer = isTruthy((vehicleData.value as any)?.certified_by_dealer)
    const mfr = isTruthy((vehicleData.value as any)?.certified_by_manufacturer)
    return (dealer || mfr || hasCpoSignal.value) && showIsCertified.value
  },
  set: (v) => {
    ;(vehicleData.value as any).certified_by_dealer = v
    if (!v) {
      ;(vehicleData.value as any).certified_by_manufacturer = false
    }
  },
})

const sameStringArray = (a: string[], b: string[]): boolean => {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

// Normalize condition to our UI values (new/used). Avoid watchEffect self-trigger recursion.
watch(
  () => (vehicleData.value as any)?.condition,
  (raw) => {
    const normalized = normalizeConditionValue(raw)
    if (normalized && raw !== normalized) {
      ;(vehicleData.value as any).condition = normalized
    }
    // Certification is a used-vehicle concept; clear if condition changes away from used.
    if (toConditionToken(normalized) !== 'Used') {
      isCertifiedUi.value = false
    }
  },
  { immediate: true },
)

// Hydrate additional conditions from SRP `conditions` (once), and detect legacy CPO signals.
watch(
  () => (vehicleData.value as any)?.conditions,
  (existing) => {
    const existingArr: string[] = Array.isArray(existing)
      ? (existing as unknown[]).map((x) => String(x)).filter(Boolean)
      : typeof existing === 'string'
        ? existing
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : []

    // If the incoming SRP doc has "CPO" in conditions, preserve that as a certified signal.
    // Keep this sticky so we don't lose "Certified" when we normalize conditions.
    if (existingArr.some((x) => String(x).trim().toUpperCase() === 'CPO')) {
      hasCpoSignal.value = true
    }

    if (additionalConditionsTouched.value) return

    const base = toConditionToken((vehicleData.value as any)?.condition)
    const extras = existingArr
      .map((x) => String(x).trim())
      .filter((t) => {
        if (!t) return false
        if (base && t.toLowerCase() === base.toLowerCase()) return false
        if (t.toLowerCase() === 'certified') return false
        if (t.toUpperCase() === 'CPO') return false
        return true
      })

    if (!sameStringArray(additionalConditions.value, extras)) {
      additionalConditions.value = extras
    }
  },
  { immediate: true, deep: true },
)

// Derive SRP `conditions` tokens from core condition + certifications + user-provided extras.
watch(
  [
    () => (vehicleData.value as any)?.condition,
    () => additionalConditions.value,
    () => isCertifiedUi.value,
    () => hasCpoSignal.value,
  ],
  () => {
    const base = toConditionToken((vehicleData.value as any)?.condition)
    const out: string[] = []
    if (base) out.push(base)

    if (base === 'Used' && isCertifiedUi.value) out.push('Certified')

    for (const raw of additionalConditions.value) {
      const t = String(raw ?? '').trim()
      if (!t) continue
      if (base && t.toLowerCase() === base.toLowerCase()) continue
      if (t.toLowerCase() === 'certified') continue
      out.push(t)
    }

    const dedup = Array.from(new Set(out))
    const current = Array.isArray((vehicleData.value as any)?.conditions)
      ? ((vehicleData.value as any).conditions as unknown[]).map((x: unknown) => String(x))
      : []

    if (!sameStringArray(current, dedup)) {
      ;(vehicleData.value as any).conditions = dedup
    }
  },
  { immediate: true, deep: true },
)

const initialWebsiteVisibility = ref<WebsiteVisibility>(WEBSITE_VISIBILITY.VISIBLE)
const selectedWebsiteVisibilityReason = ref<string>('TEMP_HOLD')
const websiteVisibilityReasonNotes = ref<string>('')
const websiteVisibilityReasonOptions = [
  { value: 'RECON', label: 'In recon' },
  { value: 'PRICING', label: 'Pricing issue' },
  { value: 'PHOTOS', label: 'Missing photos' },
  { value: 'TEMP_HOLD', label: 'Temporary hold' },
  { value: 'RESERVED', label: 'Reserved / deposit taken' },
  { value: 'SOLD_PENDING', label: 'Sold pending delivery / funding' },
  { value: 'OTHER', label: 'Other' },
]

const isNonEmpty = (v: unknown): boolean => {
  if (v == null) return false
  if (typeof v === 'string') return v.trim().length > 0
  return true
}

const liveFieldErrors = computed<Record<string, string>>(() => {
  const data = (vehicleData.value || {}) as Record<string, unknown>
  const errs: Record<string, string> = {}

  // Required SRP fields
  for (const k of REQUIRED_FIELDS) {
    if (!isNonEmpty((data as any)[k])) errs[k] = 'Required'
  }

  // VIN must be valid (17 chars, excluding I/O/Q)
  const vinRaw = String((data as any)?.vin || '').trim()
  const vinSanitized = sanitizeVin(vinRaw)
  if (!vinSanitized) {
    errs.vin = 'VIN is required.'
  } else if (!isValidVin(vinSanitized)) {
    errs.vin = 'VIN must be exactly 17 alphanumeric characters (excluding I, O, Q).'
  }

  if (initialWebsiteVisibility.value === WEBSITE_VISIBILITY.HIDDEN) {
    const r = String(selectedWebsiteVisibilityReason.value || '').trim()
    if (!r) errs.selectedWebsiteVisibilityReason = 'Required'
    if (r === 'OTHER' && !String(websiteVisibilityReasonNotes.value || '').trim()) {
      errs.websiteVisibilityReasonNotes = 'Required'
    }
  }

  return errs
})

// Merge "live" validation with runtime/submit errors (submit errors win)
const uiFieldErrors = computed<Record<string, string>>(() => {
  return { ...liveFieldErrors.value, ...fieldErrors.value }
})

const isFormValidForSave = computed(() => Object.keys(liveFieldErrors.value).length === 0)

// Organize fields into visual sections
const fieldSections = computed(() => {
  const sections = [
    {
      label: 'Essential Information',
      fields: displayFields.value.filter((f) => PRIORITY_FIELDS.essential.includes(f as any)),
    },
    {
      label: 'Pricing & Mileage',
      fields: displayFields.value.filter((f) => PRIORITY_FIELDS.pricing.includes(f as any)),
    },
    {
      label: 'Physical Characteristics',
      fields: displayFields.value.filter((f) => PRIORITY_FIELDS.physical.includes(f as any)),
    },
    {
      label: 'Specifications',
      fields: displayFields.value.filter((f) => PRIORITY_FIELDS.specifications.includes(f as any)),
    },
    {
      label: 'Description & Marketing',
      fields: displayFields.value.filter((f) => PRIORITY_FIELDS.content.includes(f as any)),
    },
    {
      label: 'Certifications',
      fields: displayFields.value.filter(
        (f) => PRIORITY_FIELDS.certifications.includes(f as any) && !hiddenCertificationFields.has(f),
      ),
    },
    {
      label: 'Features',
      fields: displayFields.value.filter((f) => PRIORITY_FIELDS.features.includes(f as any)),
    },
  ]

  // Filter out empty sections
  return sections.filter((section) => section.fields.length > 0)
})

// Conditional field visibility logic
const shouldShowField = (field: string): boolean => {
  // Show cab_style only for trucks
  if (field === 'cab_style') {
    const vehicleType = vehicleData.value.vehicle_type?.toLowerCase() || ''
    const bodyType = vehicleData.value.body_type?.toLowerCase() || ''
    return vehicleType.includes('truck') || bodyType.includes('truck')
  }

  // All other fields are shown by default
  return true
}

const triggerPhotoSelection = () => {
  const el = Array.isArray(photoInputRef.value) ? photoInputRef.value[0] : photoInputRef.value
  el?.click?.()
}

const openCameraDialog = () => {
  cameraDialogOpen.value = true
}

const addFilesToPhotos = (files: File[]) => {
  files.forEach((file) => {
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
    const previewUrl = URL.createObjectURL(file)
    const match = file.name.match(/^(.*?)(?:\.([^.]+))?$/)
    const base = (match?.[1] || file.name).replace(/\s+/g, '_')
    const ext = (match?.[2] || '').toLowerCase()
    photos.value.push({
      id,
      file,
      name: base,
      ext,
      previewUrl,
    })
  })
}

const handlePhotoSelection = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files || [])
  addFilesToPhotos(files)
  // reset input value to allow re-selecting same files
  if (input) {
    input.value = ''
  }
}

const handleCameraCapture = (files: File[]) => {
  addFilesToPhotos(files)
}

const removePhoto = (id: string) => {
  const index = photos.value.findIndex((p) => p.id === id)
  if (index >= 0) {
    URL.revokeObjectURL(photos.value[index].previewUrl)
    photos.value.splice(index, 1)
  }
}

const clearPhotos = () => {
  photos.value.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
  photos.value = []
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const value = bytes / Math.pow(1024, i)
  return `${value.toFixed(1)} ${sizes[i]}`
}

const getVinForUploads = () => {
  const candidate = (vehicleData.value.vin || vinInput.value || '').trim()
  return candidate
}

// vuedraggable manages ordering; no manual drag handlers required
const sanitizeVin = (vin: string) => vin.replace(/[^A-Za-z0-9]/g, '').toUpperCase()

const isValidVin = (vin: string) => {
  // VIN: exactly 17 chars, A–Z and 0–9, excluding I, O, Q
  // Regex uses ranges that skip I, O, Q: A-H J-N P R-Z
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/
  return vinRegex.test(vin)
}

const clearFieldError = (field: string) => {
  if (fieldErrors.value[field]) {
    const updated = { ...fieldErrors.value }
    delete updated[field]
    fieldErrors.value = updated
  }
}

const DUPLICATE_VIN_MESSAGE = 'This VIN already exists in inventory. Use Edit Vehicle instead.'

const getDuplicateVinMessage = async (vin: string): Promise<string | null> => {
  try {
    const response = await fetchVehicleByVin(endpoint, accountSettings.accountId, vin, true)
    if (!response.vehicle) return null

    return DUPLICATE_VIN_MESSAGE
  } catch (error) {
    console.warn('Unable to pre-check VIN availability before manual add:', error)
    return null
  }
}

// Fetch available fields from API
const fetchFields = async () => {
  isLoadingFields.value = true
  try {
    const resp = await fetchIseMappingFields(endpoint, accountSettings.accountId)
    const keys = extractIseMappingFieldKeys(resp)
    // If the API fails or returns an unexpected shape, fall back to our known display order
    // so the manual add/edit UX still renders the expected fields.
    availableFields.value = keys.length ? keys : [...(DISPLAY_FIELD_ORDER as unknown as string[])]

    // Use configured display fields (priority fields only, in order)
    // VIN and condition are handled separately in the UI
    const excludedSet = new Set([...EXCLUDED_FIELDS, 'vin', 'condition'])
    displayFields.value = getManualAddEditDisplayFields(availableFields.value).filter(
      (field) => !excludedSet.has(field)
    )
  } catch (error) {
    console.error('Error fetching fields:', error)
    makeAlert('Failed to load vehicle fields', 'error')
  } finally {
    isLoadingFields.value = false
  }
}

async function loadEnhancementOptionsForDealer(): Promise<void> {
  enhancementsError.value = ''
  enhancementOptions.value = []
  selectedEnhancementId.value = ''

  try {
    const res = await fetch(`${endpoint}/dealer-feeds/${encodeURIComponent(accountSettings.accountId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new Error(`Failed to load dealer feeds (HTTP ${res.status})`)
    const json = await res.json()
    const feeds = Array.isArray(json?.feeds) ? json.feeds : []
    const enhancements = feeds.filter((f: any) => f?.type === 'enhancement')
    const options: EnhancementOption[] = enhancements.map((f: any) => ({
      value: String(f?.id || '').trim(),
      title: String(f?.friendlyName || f?.id || '').trim() || String(f?.id || '').trim(),
    }))
    enhancementOptions.value = options.filter((o) => o.value)
    // Default-select first enhancement for faster flow
    if (enhancementOptions.value.length > 0) {
      selectedEnhancementId.value = enhancementOptions.value[0].value
    }
  } catch (e) {
    enhancementsError.value = e instanceof Error ? e.message : String(e)
    enhancementOptions.value = []
    selectedEnhancementId.value = ''
  }
}

async function loadTrimsForYmm(): Promise<void> {
  ymmError.value = ''
  trimOptions.value = []
  selectedTrimId.value = ''

  const year = String(ymmYear.value || '').trim()
  const make = String(ymmMake.value || '').trim()
  const model = String(ymmModel.value || '').trim()
  if (!year || !make || !model) {
    ymmError.value = 'Year, make, and model are required.'
    return
  }

  try {
    const response = await fetch(`${endpoint}/custom-feed-prefill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        juid: accountSettings.accountId,
        year,
        make,
        model,
      }),
    })

    if (!response.ok) {
      let msg = `Failed to load trims (HTTP ${response.status})`
      try {
        const j = await response.json()
        if (j?.message) msg = String(j.message)
      } catch {}
      throw new Error(msg)
    }

    const data = await response.json()
    if (data?.kind !== 'model_trims') {
      throw new Error('Unexpected response while loading trims')
    }

    const trims = Array.isArray(data?.trims) ? data.trims : []
    trimOptions.value = trims
      .map((t: any) => {
        const id = String(t?.id || '').trim()
        const trim = String(t?.trim || '').trim()
        const styleName = String(t?.styleName || '').trim()
        const msrp = typeof t?.baseMsrp === 'number' ? t.baseMsrp : Number(t?.baseMsrp) || null
        const title = msrp ? `${trim} — $${msrp.toLocaleString()}` : trim || styleName || id
        return { value: id, title, trim, styleName, baseMsrp: msrp, img: t?.img || null }
      })
      .filter((o: any) => o.value && o.trim)

    if (trimOptions.value.length > 0) {
      selectedTrimId.value = trimOptions.value[0].value
    }
  } catch (e) {
    ymmError.value = e instanceof Error ? e.message : String(e)
  }
}

const continueFromEntry = async () => {
  vinError.value = ''
  ymmError.value = ''
  prefillStatus.value = null
  prefillMessage.value = ''

  if (entryMode.value === 'vin') {
    const sanitizedVin = sanitizeVin(vinInput.value)
    vinInput.value = sanitizedVin
    if (!isValidVin(sanitizedVin)) {
      vinError.value =
        'Please enter a valid VIN (exactly 17 alphanumeric characters, excluding I, O, Q)'
      return
    }

    const duplicateVinMessage = await getDuplicateVinMessage(sanitizedVin)
    if (duplicateVinMessage) {
      vinError.value = duplicateVinMessage
      makeAlert(duplicateVinMessage, 'error')
      return
    }
  } else {
    const year = String(ymmYear.value || '').trim()
    const make = String(ymmMake.value || '').trim()
    const model = String(ymmModel.value || '').trim()
    if (!year || !make || !model) {
      ymmError.value = 'Year, make, and model are required.'
      return
    }
  }

  isLoadingPrefill.value = true
  try {
    await loadEnhancementOptionsForDealer()
    step.value = 'enhancement-select'
  } finally {
    isLoadingPrefill.value = false
  }
}

const resetToEntry = () => {
  enhancementFailedDialog.value = false
  enhancementFailedMessage.value = ''
  enhancementsError.value = ''
  enhancementOptions.value = []
  selectedEnhancementId.value = ''
  ymmError.value = ''
  trimOptions.value = []
  selectedTrimId.value = ''
  prefillStatus.value = null
  prefillMessage.value = ''
  step.value = 'vin-input'
}

const continueFromTrim = async () => {
  const selected = trimOptions.value.find((o) => o.value === selectedTrimId.value)
  if (!selected) return

  isLoadingPrefill.value = true
  prefillStatus.value = null
  prefillMessage.value = ''

  try {
    const year = String(ymmYear.value || '').trim()
    const make = String(ymmMake.value || '').trim()
    const model = String(ymmModel.value || '').trim()
    const response = await fetch(`${endpoint}/custom-feed-prefill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        juid: accountSettings.accountId,
        year,
        make,
        model,
        trimId: selected.value,
      }),
    })

    if (!response.ok) {
      let msg = `Failed to prefill from trim (HTTP ${response.status})`
      try {
        const j = await response.json()
        if (j?.message) msg = String(j.message)
      } catch {}
      throw new Error(msg)
    }

    const data = await response.json()
    const sanitized = sanitizeAddVehicleEnhancementPrefill(data)
    if (!sanitized.hasUsableEnhancementContent) {
      enhancementFailedMessage.value = 'No data was returned from the selected trim.'
      enhancementFailedDialog.value = true
      return
    }

    vehicleData.value = {
      ...vehicleData.value,
      ...sanitized.values,
    }
    ensureDefaultOperationalInventoryFields()

    if (!displayFields.value.length && !isLoadingFields.value) {
      await fetchFields()
    }

    prefillStatus.value = 'success'
    prefillMessage.value = `Prefilled ${sanitized.usableCount} field(s) from selected trim.`
    step.value = 'form'
  } catch (e) {
    console.error('Error prefilling from trim:', e)
    enhancementFailedMessage.value = e instanceof Error ? e.message : String(e)
    enhancementFailedDialog.value = true
  } finally {
    isLoadingPrefill.value = false
  }
}

const backToEnhancement = () => {
  step.value = 'enhancement-select'
}

const continueManualFromEnhancement = async () => {
  // Proceed without enhancement; user can fill fields manually.
  vehicleData.value = {
    ...vehicleData.value,
    vin: sanitizeVin(vinInput.value || (vehicleData.value as any)?.vin || ''),
    year:
      entryMode.value === 'ymm'
        ? Number(String(ymmYear.value).trim()) || undefined
        : (vehicleData.value as any)?.year,
    make: entryMode.value === 'ymm' ? String(ymmMake.value || '').trim() : (vehicleData.value as any)?.make,
    model: entryMode.value === 'ymm' ? String(ymmModel.value || '').trim() : (vehicleData.value as any)?.model,
  }
  ensureDefaultOperationalInventoryFields()

  if (!displayFields.value.length && !isLoadingFields.value) {
    await fetchFields()
  }

  prefillStatus.value = null
  prefillMessage.value = ''
  step.value = 'form'
}

const continueFromEnhancement = async () => {
  // If no enhancements configured, treat as manual flow.
  if (enhancementOptions.value.length === 0) {
    await continueManualFromEnhancement()
    return
  }

  const chosen = String(selectedEnhancementId.value || '').trim().toLowerCase()
  const normalizedEnhancement = chosen.startsWith('import_') ? chosen.slice('import_'.length) : chosen

  if (entryMode.value === 'ymm') {
    // Chrome only for Y/M/M right now.
    if (normalizedEnhancement !== 'chrome') {
      enhancementFailedMessage.value =
        'This enhancement requires a VIN. Choose Chrome for Year/Make/Model, or switch to VIN entry.'
      enhancementFailedDialog.value = true
      return
    }

    isLoadingPrefill.value = true
    try {
      await loadTrimsForYmm()
      if (ymmError.value) {
        enhancementFailedMessage.value = ymmError.value
        enhancementFailedDialog.value = true
        return
      }
      step.value = 'trim-select'
    } finally {
      isLoadingPrefill.value = false
    }
    return
  }

  // VIN mode: run the enhancement now (Chrome or any future enhancement id).
  await attemptEnhancement()
}

const continueManualFromFailure = async () => {
  enhancementFailedDialog.value = false
  const sanitizedVin = sanitizeVin(vinInput.value)
  vehicleData.value = { ...vehicleData.value, vin: sanitizedVin }
  ensureDefaultOperationalInventoryFields()
  if (!displayFields.value.length && !isLoadingFields.value) {
    await fetchFields()
  }
  prefillStatus.value = 'error'
  prefillMessage.value =
    enhancementFailedMessage.value ||
    'We could not enhance data automatically. Please complete the fields manually.'
  step.value = 'form'
}

const startOverFromFailure = () => {
  enhancementFailedDialog.value = false
  enhancementFailedMessage.value = ''
  vinInput.value = ''
  vinError.value = ''
  entryMode.value = 'vin'
  ymmYear.value = ''
  ymmMake.value = ''
  ymmModel.value = ''
  ymmError.value = ''
  trimOptions.value = []
  selectedTrimId.value = ''
  resetToEntry()
}

// Step 2 -> Step 3: attempt enhancement prefill
const attemptEnhancement = async () => {
  const sanitizedVin = sanitizeVin(vinInput.value)
  vinInput.value = sanitizedVin
  if (!isValidVin(sanitizedVin)) {
    vinError.value =
      'Please enter a valid VIN (exactly 17 alphanumeric characters, excluding I, O, Q)'
    return
  }

  // If there are enhancements but nothing selected, do nothing.
  if (enhancementOptions.value.length > 0 && !selectedEnhancementId.value) return

  isLoadingPrefill.value = true
  enhancementFailedMessage.value = ''
  try {
    const response = await fetch(`${endpoint}/custom-feed-prefill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        juid: accountSettings.accountId,
        vin: sanitizedVin,
        enhancementId: selectedEnhancementId.value || undefined,
      }),
    })

    if (!response.ok) {
      let msg = 'Failed to enhance vehicle data'
      try {
        const j = await response.json()
        if (j?.message) msg = String(j.message)
      } catch {}
      enhancementFailedMessage.value = msg
      enhancementFailedDialog.value = true
      return
    }

    const data = await response.json()
    const sanitized = sanitizeAddVehicleEnhancementPrefill(data)

    if (!sanitized.hasUsableEnhancementContent) {
      enhancementFailedMessage.value =
        'No data was returned from the selected enhancement for this VIN.'
      enhancementFailedDialog.value = true
      return
    }

    const prefilledVin = sanitized.values.vin
    vehicleData.value = {
      ...vehicleData.value,
      ...sanitized.values,
      vin: sanitizeVin(typeof prefilledVin === 'string' && prefilledVin.trim() ? prefilledVin : sanitizedVin),
    }
    ensureDefaultOperationalInventoryFields()

    if (!displayFields.value.length && !isLoadingFields.value) {
      await fetchFields()
    }

    prefillStatus.value = 'success'
    const enhancementLabel =
      enhancementOptions.value.find((o) => o.value === selectedEnhancementId.value)?.title ||
      'enhancement'
    prefillMessage.value = `Vehicle data prefilled successfully from ${enhancementLabel}!`
    step.value = 'form'
    makeAlert('Vehicle data prefilled successfully!', 'success')
  } catch (error) {
    console.error('Error enhancing vehicle data:', error)
    enhancementFailedMessage.value =
      error instanceof Error ? error.message : 'Failed to enhance vehicle data'
    enhancementFailedDialog.value = true
  } finally {
    isLoadingPrefill.value = false
  }
}

// Save vehicle
const saveVehicle = async () => {
  if (isSaving.value) return
  isSaving.value = true

  const photosToUpload = [...photos.value]
  let uploadedImages: Array<{ fileName: string; publicUrl: string; key: string }> = []
  fieldErrors.value = {}

  try {
    savingDialog.value = true
    savingMessage.value = photosToUpload.length
      ? 'Requesting upload URLs...'
      : 'Saving vehicle details...'
    savingProgress.value = photosToUpload.length ? 5 : 20

    const rawVin = getVinForUploads()
    const sanitizedVin = sanitizeVin(rawVin)
    if (!sanitizedVin) {
      savingDialog.value = false
      isSaving.value = false
      fieldErrors.value = { ...fieldErrors.value, vin: 'VIN is required.' }
      makeAlert('VIN is required to save the vehicle. Please provide a VIN.', 'error')
      return
    }
    if (!isValidVin(sanitizedVin)) {
      savingDialog.value = false
      isSaving.value = false
      fieldErrors.value = {
        ...fieldErrors.value,
        vin: 'VIN must be exactly 17 alphanumeric characters (excluding I, O, Q).',
      }
      makeAlert('VIN must be exactly 17 alphanumeric characters (excluding I, O, Q).', 'error')
      return
    }
    vinInput.value = sanitizedVin
    vehicleData.value.vin = sanitizedVin

    const duplicateVinMessage = await getDuplicateVinMessage(sanitizedVin)
    if (duplicateVinMessage) {
      savingDialog.value = false
      isSaving.value = false
      fieldErrors.value = {
        ...fieldErrors.value,
        vin: duplicateVinMessage,
      }
      makeAlert(duplicateVinMessage, 'error')
      return
    }

    if (initialWebsiteVisibility.value === WEBSITE_VISIBILITY.HIDDEN) {
      const r = String(selectedWebsiteVisibilityReason.value || '').trim()
      if (!r) {
        savingDialog.value = false
        isSaving.value = false
        makeAlert('Please select an initial hidden reason.', 'error')
        return
      }
      if (r === 'OTHER' && !String(websiteVisibilityReasonNotes.value || '').trim()) {
        savingDialog.value = false
        isSaving.value = false
        makeAlert('Please specify the hidden reason.', 'error')
        return
      }
    }

    // Validate required SRP fields before any uploads
    const missing: string[] = REQUIRED_FIELDS.filter((key) => {
      const value = (vehicleData.value as Record<string, any>)[key]
      if (value === null || value === undefined) return true
      if (typeof value === 'string' && value.trim().length === 0) return true
      return false
    })
    if (missing.length > 0) {
      // Mark field errors
      const errs: Record<string, string> = {}
      missing.forEach((k) => (errs[k] = 'Required'))
      fieldErrors.value = { ...fieldErrors.value, ...errs }
      savingDialog.value = false
      isSaving.value = false
      const msg = `The following required fields are missing: ${missing.join(', ')}. These core fields are required for custom feed.`
      makeAlert(msg, 'error')
      return
    }

    // 1) Save vehicle details FIRST (without images) to avoid orphaned uploads
    savingMessage.value = 'Saving vehicle details...'
    savingProgress.value = Math.max(savingProgress.value, 25)

    // Build payload using only SRP-supported fields (+ vin), excluding dealer_photos initially
    // NOTE: `conditions` is used by SRP/OS facets as a multi-value "Conditions" bucket.
    const allowedKeysBase = new Set<string>([...(availableFields.value || []), 'vin', 'conditions'])
    const filteredBase: Record<string, any> = {}
    Object.entries(vehicleData.value || {}).forEach(([key, value]) => {
      // A6: operational/workflow fields must never enter the SRP custom-feed field set. Operational
      // inventory attributes (inventoryState/inventoryUsageType/website visibility) are passed
      // separately below as explicit operational create metadata.
      if (isExcludedFromSrpPatch(key)) return
      if (allowedKeysBase.has(key)) filteredBase[key] = value
    })
    filteredBase.vin = filteredBase.vin || sanitizedVin

    // A6 operational inventory attributes are dealer-authored on Add, but persisted operationally
    // (CurrentInventory / draft operationalPatch) — not as SRP/custom-feed display fields. The
    // backend extracts these from the create payload and projects inventoryState -> SRP.state.
    const operationalCreateMetadata: Record<string, unknown> = {}
    const addInventoryState = (vehicleData.value as any)?.inventoryState
    const addInventoryUsageType = (vehicleData.value as any)?.inventoryUsageType
    if (addInventoryState !== undefined && addInventoryState !== null && String(addInventoryState).trim() !== '') {
      operationalCreateMetadata.inventoryState = addInventoryState
    }
    if (addInventoryUsageType !== undefined && addInventoryUsageType !== null && String(addInventoryUsageType).trim() !== '') {
      operationalCreateMetadata.inventoryUsageType = addInventoryUsageType
    }
    const websiteVisibilityReason =
      initialWebsiteVisibility.value === WEBSITE_VISIBILITY.HIDDEN
        ? selectedWebsiteVisibilityReason.value === 'OTHER'
          ? String(websiteVisibilityReasonNotes.value || '').trim()
          : String(selectedWebsiteVisibilityReason.value || '').trim()
        : undefined

    const basePayload = {
      ...filteredBase,
      ...operationalCreateMetadata,
      jazelId: accountSettings.accountId,
      createOnly: true,
      websiteVisibility: initialWebsiteVisibility.value,
      websiteVisibilityReason,
    }

    const response = await fetch(`${endpoint}/custom-feed`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(basePayload),
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null)
      throw new Error(errorBody?.message || 'Failed to save vehicle')
    }

    // 2) If there are photos to upload, upload then update dealer_photos
    if (photosToUpload.length > 0) {
      const timestampBase = Date.now()
      const generatedFileNames = photosToUpload.map((photo, index) => {
        const providedBase = (photo.name || '').trim()
        const base =
          providedBase.length > 0 ? providedBase : `${sanitizedVin}_${timestampBase}_${index + 1}`
        const ext = (photo.ext || photo.file.name.match(/\.([^.]+)$/)?.[1] || 'jpg').toLowerCase()
        return `${base}.${ext}`
      })

      savingMessage.value = 'Requesting upload URLs...'
      const presignedResponse = await fetch(`${endpoint}/upload-vehicle-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          juid: accountSettings.accountId,
          vin: sanitizedVin,
          imageCount: photosToUpload.length,
          fileNames: generatedFileNames,
        }),
      })
      if (!presignedResponse.ok) throw new Error('Failed to request upload URLs')

      const presignedData = await presignedResponse.json()
      const urls = Array.isArray(presignedData?.urls) ? presignedData.urls : []
      if (urls.length !== photosToUpload.length) {
        throw new Error('Upload URL count mismatch')
      }

      const totalSteps = photosToUpload.length + 2
      let completedSteps = 1
      savingProgress.value = Math.round((completedSteps / totalSteps) * 100)

      uploadedImages = []
      for (let i = 0; i < photosToUpload.length; i++) {
        const photo = photosToUpload[i]
        const uploadInfo = urls[i] || {}
        const uploadUrl: string | undefined = uploadInfo.uploadUrl
        const publicUrl: string | undefined = uploadInfo.publicUrl
        const key: string | undefined = uploadInfo.key
        const fileName: string = uploadInfo.fileName || generatedFileNames[i]

        if (!uploadUrl) {
          throw new Error(`Missing upload URL for ${fileName}`)
        }

        savingMessage.value = `Uploading ${fileName} (${i + 1}/${photosToUpload.length})...`
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: photo.file,
        })
        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${fileName}`)
        }

        uploadedImages.push({
          fileName,
          publicUrl: publicUrl || '',
          key: key || '',
        })

        completedSteps += 1
        savingProgress.value = Math.round((completedSteps / totalSteps) * 100)
      }

      // After uploads, update the record with dealer_photos
      const publicUrls = uploadedImages.map((image) => image.publicUrl).filter(Boolean)
      const updatePayload = {
        ...filteredBase,
        ...operationalCreateMetadata,
        vin: sanitizedVin,
        dealer_photos: publicUrls,
        jazelId: accountSettings.accountId,
        websiteVisibility: initialWebsiteVisibility.value,
        websiteVisibilityReason,
      }
      savingMessage.value = 'Finalizing images...'
      const updateResp = await fetch(`${endpoint}/custom-feed`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(updatePayload),
      })
      if (!updateResp.ok) {
        const errorBody = await updateResp.json().catch(() => null)
        throw new Error(errorBody?.message || 'Failed to associate images with vehicle')
      }
    }

    savingMessage.value = 'Vehicle saved successfully!'
    savingProgress.value = 100
    makeAlert('Vehicle saved successfully!', 'success')

    // Ensure the left-nav "Unpublished Changes" link appears immediately for draft vehicles.
    // (Draft vehicles are publishable even if there are no config changes.)
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      // Non-fatal; nav will update on refresh/next poll.
      console.warn('⚠️ Failed to refresh draft vehicle count (non-fatal):', e)
    }

    // Note: We no longer mark inventory as "unpublished changes" since draft vehicles
    // are already visible on the Inventory page with a "Draft" indicator.
    // They can be managed/deleted from Vehicle Details page if needed.

    clearPhotos()
    router.push({ name: 'VehicleDetails', params: { vin: sanitizedVin } })
  } catch (error) {
    console.error('Error saving vehicle:', error)
    makeAlert(error instanceof Error ? error.message : 'Failed to save vehicle', 'error')
  } finally {
    savingDialog.value = false
    isSaving.value = false
  }
}

// Format field label
const formatFieldLabel = (field: string): string => {
  const label = field
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  
  // Add asterisk for required fields
  return REQUIRED_FIELDS.includes(field) ? `${label} *` : label
}

// Go back
const goBack = () => {
  router.push('/inventory')
}

onMounted(async () => {
  await fetchFields()

  // Optional Phase 1 handoff: if /inventory/add?vin=... is provided, prefill VIN entry.
  const qVin = String((route.query as any)?.vin || '').trim()
  if (qVin) {
    entryMode.value = 'vin'
    vinInput.value = sanitizeVin(qVin)
  }
})

onBeforeUnmount(() => {
  clearPhotos()
})
</script>

<style scoped>
.photo-table tbody tr td {
  vertical-align: middle;
}
.drag-handle {
  cursor: grab;
  user-select: none;
  -webkit-user-drag: element;
}
.drag-chosen .drag-handle {
  cursor: grabbing;
}
.drag-ghost {
  opacity: 0.5;
}

.form-footer-spacer {
  height: 88px;
}

.form-footer-sticky {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.08);
}

.form-footer-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 24px;
}
</style>
